/**
 * Panel de Notificaciones/Avisos - Rediseño Trello-style
 * Avatares de empleado, tarjetas limpias, jerarquía visual clara
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Bell, X, Check, CheckCheck, AlertTriangle, Info, Clock,
  Trash2, Send, Plus, User, MessageSquare, ChevronLeft,
  Filter, Loader2, Eye, EyeOff, ArrowRight, MoreHorizontal, Mail,
  Settings, Package
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAvisoTemporal, AvisoInline, DialogoConfirmar } from './empleados/RegistroHorarioEmpleado';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Color palette for avatars (10 distinctive colors) ─── */
const AVATAR_COLORS = [
  '#2563eb', // blue
  '#7c3aed', // violet
  '#db2777', // pink
  '#ea580c', // orange
  '#0d9488', // teal
  '#4f46e5', // indigo
  '#059669', // emerald
  '#dc2626', // red
  '#ca8a04', // yellow-dark
  '#6d28d9', // purple
];

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitial = (name) => {
  if (!name || name === 'Sistema' || name === 'Sistema de Alertas' || name === 'Asistente Director IA') return 'S';
  return name.charAt(0).toUpperCase();
};

const isSystemSender = (name) => {
  return !name || name === 'Sistema' || name === 'Sistema de Alertas' || name === 'Asistente Director IA';
};

/* ─── Avatar component ─── */
const Avatar = ({ name, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-6 h-6 text-[10px]',
    md: 'w-8 h-8 text-xs',
    lg: 'w-10 h-10 text-sm',
  };
  const isSys = isSystemSender(name);
  const bg = isSys ? '#64748b' : getAvatarColor(name);
  const initial = getInitial(name);

  return (
    <div
      className={`${sizeClasses[size]} rounded-full flex items-center justify-center font-semibold text-white flex-shrink-0 select-none`}
      style={{ backgroundColor: bg }}
      title={name || 'Sistema'}
      data-testid={`avatar-${(name || 'sistema').toLowerCase().replace(/\s+/g, '-')}`}
    >
      {isSys ? (
        <Bell className={size === 'sm' ? 'w-3 h-3' : size === 'md' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
      ) : (
        initial
      )}
    </div>
  );
};

/* ─── Config constants ─── */
const TIPO_CONFIG = {
  info: { icon: Info, color: '#3b82f6', bg: '#eff6ff', label: 'Aviso' },
  alerta: { icon: AlertTriangle, color: '#f59e0b', bg: '#fffbeb', label: 'Alerta' },
  urgente: { icon: AlertTriangle, color: '#ef4444', bg: '#fef2f2', label: 'Urgente' },
  tarea: { icon: Clock, color: '#8b5cf6', bg: '#f5f3ff', label: 'Tarea' },
  asignacion_evento: { icon: Info, color: '#10b981', bg: '#ecfdf5', label: 'Asignacion' },
  material_insuficiente: { icon: Package, color: '#ea580c', bg: '#fff7ed', label: 'Material' },
};

const ESTADO_CONFIG = {
  pendiente: { label: 'Pendiente', color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400', ring: 'ring-slate-200' },
  en_progreso: { label: 'En progreso', color: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500', ring: 'ring-blue-200' },
  completada: { label: 'Completada', color: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500', ring: 'ring-emerald-200' },
  bloqueada: { label: 'Bloqueada', color: 'bg-red-50 text-red-700', dot: 'bg-red-500', ring: 'ring-red-200' },
};

const PRIORIDAD_STYLES = {
  baja: { border: 'border-l-slate-300', label: 'Baja', bg: '' },
  normal: { border: 'border-l-blue-400', label: 'Normal', bg: '' },
  alta: { border: 'border-l-amber-400', label: 'Alta', bg: 'bg-amber-50/40' },
  urgente: { border: 'border-l-red-500', label: 'Urgente', bg: 'bg-red-50/30' },
};

/* ─── Time formatting ─── */
const formatFecha = (fecha) => {
  if (!fecha) return '';
  const d = new Date(fecha);
  const ahora = new Date();
  const diff = ahora - d;
  if (diff < 60000) return 'Ahora';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
  if (diff < 172800000) return 'Ayer';
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

const formatFechaCompleta = (fecha) => {
  if (!fecha) return '';
  return new Date(fecha).toLocaleString('es-ES', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

/* ═══════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                             */
/* ═══════════════════════════════════════════════════════════ */
const NotificacionesSidebar = ({ collapsed = false }) => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [notificaciones, setNotificaciones] = useState([]);
  const [enviadas, setEnviadas] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [mostrarPanel, setMostrarPanel] = useState(false);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [tab, setTab] = useState('recibidas');
  const [destinatarios, setDestinatarios] = useState({ grupos: [], empleados: [] });
  const [enviando, setEnviando] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [mostrarCompletadas, setMostrarCompletadas] = useState(false);
  const [prefs, setPrefs] = useState(null); // { tipos: [...], tipos_ocultos: [...] }
  const [mostrarPrefs, setMostrarPrefs] = useState(false);
  const [emailToasts, setEmailToasts] = useState([]);
  const comentariosRef = useRef(null);
  const toastTimeoutsRef = useRef({});
  // Confirmación de borrado y avisos propios (sin los cuadros del navegador)
  const [aEliminar, setAEliminar] = useState(null); // id del aviso a eliminar
  const [eliminando, setEliminando] = useState(false);
  const { aviso: avisoForm, mostrarAviso: mostrarAvisoForm, limpiarAviso: limpiarAvisoForm } = useAvisoTemporal(5000);

  /* ─── Toasts in-app de correo nuevo ─── */
  const cerrarEmailToast = useCallback((id) => {
    setEmailToasts(prev => prev.filter(t => t.id !== id));
    if (toastTimeoutsRef.current[id]) {
      clearTimeout(toastTimeoutsRef.current[id]);
      delete toastTimeoutsRef.current[id];
    }
  }, []);

  const agregarEmailToasts = useCallback((emails) => {
    emails.forEach((e) => {
      setEmailToasts(prev => {
        if (prev.some(t => t.id === e.id)) return prev;
        return [...prev.slice(-2), e]; // máximo 3 visibles
      });
      if (!toastTimeoutsRef.current[e.id]) {
        toastTimeoutsRef.current[e.id] = setTimeout(() => cerrarEmailToast(e.id), 12000);
      }
    });
  }, [cerrarEmailToast]);

  const [nuevoAviso, setNuevoAviso] = useState({
    titulo: '', mensaje: '', tipo: 'info', destinatario: '', prioridad: 'normal',
  });

  /* ─── Data fetching ─── */
  const cargarNotificaciones = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/notificaciones?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setNotificaciones(data.notificaciones || []);
        setNoLeidas(data.no_leidas || 0);
      }
    } catch (e) { console.error('Error cargando notificaciones:', e); }
  }, [token]);

  const cargarEnviadas = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/notificaciones/enviadas?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setEnviadas(data.notificaciones || []);
    } catch (e) { console.error('Error cargando enviadas:', e); }
  }, [token]);

  const cargarDestinatarios = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/notificaciones/destinatarios`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setDestinatarios({ grupos: data.grupos || [], empleados: data.empleados || [] });
    } catch (e) { console.error('Error cargando destinatarios:', e); }
  }, [token]);

  const cargarPreferencias = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/notificaciones/preferencias`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setPrefs({ tipos: data.tipos || [], tipos_ocultos: data.tipos_ocultos || [] });
    } catch (e) { console.error('Error cargando preferencias:', e); }
  }, [token]);

  const toggleTipoOculto = async (tipo) => {
    if (!prefs) return;
    const ocultos = prefs.tipos_ocultos.includes(tipo)
      ? prefs.tipos_ocultos.filter((t) => t !== tipo)
      : [...prefs.tipos_ocultos, tipo];
    setPrefs({ ...prefs, tipos_ocultos: ocultos });
    try {
      await fetch(`${API_URL}/api/notificaciones/preferencias`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tipos_ocultos: ocultos }),
      });
      cargarNotificaciones();
    } catch (e) { console.error('Error guardando preferencias:', e); }
  };

  const wsRef = useRef(null);
  const unmountedRef = useRef(false);

  useEffect(() => {
    unmountedRef.current = false;
    cargarNotificaciones();
    cargarDestinatarios();
    cargarPreferencias();
    const interval = setInterval(cargarNotificaciones, 60000);

    // ─── WebSocket connection (delayed to avoid premature close) ───
    let wsTimeout = null;
    if (token) {
      const wsProtocol = API_URL.startsWith('https') ? 'wss' : 'ws';
      const wsHost = API_URL.replace(/^https?:\/\//, '');
      const wsUrl = `${wsProtocol}://${wsHost}/api/ws/notificaciones?token=${token}`;
      
      const connectWs = () => {
        if (unmountedRef.current) return;
        try {
          const ws = new WebSocket(wsUrl);
          wsRef.current = ws;
          ws.onopen = () => { if (!unmountedRef.current) console.log('WS notificaciones conectado'); };
          ws.onmessage = (event) => {
            try {
              const msg = JSON.parse(event.data);
              if (msg.type === 'nueva_notificacion' || msg.type === 'estado_cambiado' || msg.type === 'nuevo_comentario') {
                cargarNotificaciones();
                cargarEnviadas();
              }
              if (msg.type === 'nuevo_email') {
                agregarEmailToasts(msg.emails || []);
              }
            } catch (e) { /* ignore */ }
          };
          ws.onclose = () => {
            if (!unmountedRef.current) {
              wsTimeout = setTimeout(connectWs, 5000);
            }
          };
          ws.onerror = () => { /* onclose handles reconnect */ };
        } catch (e) { /* ignore */ }
      };
      // Delay initial connection to avoid race with React strict mode
      wsTimeout = setTimeout(connectWs, 1000);
    }

    return () => {
      unmountedRef.current = true;
      clearInterval(interval);
      if (wsTimeout) clearTimeout(wsTimeout);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      Object.values(toastTimeoutsRef.current).forEach((t) => clearTimeout(t));
      toastTimeoutsRef.current = {};
    };
  }, [token, cargarNotificaciones, cargarDestinatarios, cargarPreferencias, cargarEnviadas, agregarEmailToasts]);

  useEffect(() => {
    if (tab === 'enviadas') cargarEnviadas();
  }, [tab, cargarEnviadas]);

  /* ─── Actions ─── */
  const marcarLeida = async (notifId) => {
    try {
      await fetch(`${API_URL}/api/notificaciones/${notifId}/leer`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` },
      });
      cargarNotificaciones();
      if (detalle?.id === notifId) cargarDetalle(notifId);
    } catch (e) { console.error('Error:', e); }
  };

  const marcarTodasLeidas = async () => {
    try {
      await fetch(`${API_URL}/api/notificaciones/leer-todas`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` },
      });
      cargarNotificaciones();
    } catch (e) { console.error('Error:', e); }
  };

  // Pide confirmación con el diálogo propio; el borrado va en confirmarEliminar
  const eliminarNotificacion = (notifId) => setAEliminar(notifId);

  const confirmarEliminar = async () => {
    const notifId = aEliminar;
    if (!notifId) return;
    setEliminando(true);
    try {
      await fetch(`${API_URL}/api/notificaciones/${notifId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      cargarNotificaciones();
      cargarEnviadas();
      if (detalle?.id === notifId) setDetalle(null);
    } catch (e) { console.error('Error:', e); }
    setEliminando(false);
    setAEliminar(null);
  };

  const cambiarEstado = async (notifId, nuevoEstado) => {
    try {
      await fetch(`${API_URL}/api/notificaciones/${notifId}/estado`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado: nuevoEstado }),
      });
      cargarNotificaciones();
      cargarEnviadas();
      if (detalle?.id === notifId) cargarDetalle(notifId);
    } catch (e) { console.error('Error:', e); }
  };

  const enviarComentario = async (notifId) => {
    if (!nuevoComentario.trim()) return;
    setEnviandoComentario(true);
    try {
      await fetch(`${API_URL}/api/notificaciones/${notifId}/comentario`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ texto: nuevoComentario.trim() }),
      });
      setNuevoComentario('');
      cargarDetalle(notifId);
    } catch (e) { console.error('Error:', e); }
    setEnviandoComentario(false);
  };

  const cargarDetalle = async (notifId) => {
    try {
      const res = await fetch(`${API_URL}/api/notificaciones/${notifId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setDetalle(data.notificacion);
        setTimeout(() => comentariosRef.current?.scrollTo({ top: comentariosRef.current.scrollHeight, behavior: 'smooth' }), 100);
      }
    } catch (e) { console.error('Error:', e); }
  };

  const enviarAviso = async (e) => {
    e.preventDefault();
    if (!nuevoAviso.destinatario || !nuevoAviso.mensaje.trim()) {
      mostrarAvisoForm('error', 'Selecciona un destinatario y escribe el mensaje.');
      return;
    }
    limpiarAvisoForm();
    setEnviando(true);
    try {
      const res = await fetch(`${API_URL}/api/notificaciones`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          titulo: nuevoAviso.titulo || `Aviso de ${user?.nombre || 'Usuario'}`,
          mensaje: nuevoAviso.mensaje,
          tipo: nuevoAviso.tipo,
          destinatario: nuevoAviso.destinatario,
          prioridad: nuevoAviso.prioridad,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setNuevoAviso({ titulo: '', mensaje: '', tipo: 'info', destinatario: '', prioridad: 'normal' });
        setMostrarFormulario(false);
        cargarNotificaciones();
        cargarEnviadas();
      }
    } catch (e) { console.error('Error:', e); }
    setEnviando(false);
  };

  const isAdmin = user?.rol === 'direccion' || user?.rol === 'admin';

  const listaFiltrada = (lista) => {
    let resultado = lista;
    if (!mostrarCompletadas) {
      resultado = resultado.filter((n) => (n.estado || 'pendiente') !== 'completada');
    }
    if (filtroEstado !== 'todos') {
      resultado = resultado.filter((n) => (n.estado || 'pendiente') === filtroEstado);
    }
    return resultado;
  };

  const numCompletadas = (lista) => lista.filter((n) => (n.estado || 'pendiente') === 'completada').length;

  /* ═══════════════════════════════════════════════ */
  /*  DETAIL VIEW                                     */
  /* ═══════════════════════════════════════════════ */
  const renderDetalle = () => {
    if (!detalle) return null;
    const tipoConf = TIPO_CONFIG[detalle.tipo] || TIPO_CONFIG.info;
    const estado = ESTADO_CONFIG[detalle.estado || 'pendiente'];
    const esMia = detalle.creado_por === user?.id;
    const comentarios = detalle.comentarios || [];

    return (
      <div className="flex flex-col h-full bg-slate-50">
        {/* Header */}
        <div className="px-4 py-3 bg-white border-b border-slate-200 flex items-center gap-3">
          <button
            onClick={() => setDetalle(null)}
            className="p-2.5 -m-1 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Volver"
            data-testid="notif-detalle-volver"
          >
            <ChevronLeft className="w-5 h-5 text-slate-500" />
          </button>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm text-slate-800 truncate">{detalle.titulo}</h3>
            <span className="text-[11px] text-slate-400">{formatFechaCompleta(detalle.fecha_creacion)}</span>
          </div>
          {isAdmin && (
            <button
              onClick={() => eliminarNotificacion(detalle.id)}
              className="p-2.5 -m-1 hover:bg-red-50 rounded-lg transition-colors"
              aria-label="Eliminar aviso"
              data-testid="notif-eliminar"
            >
              <Trash2 className="w-5 h-5 text-red-400" />
            </button>
          )}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto" ref={comentariosRef}>
          {/* Main info card */}
          <div className="m-3 bg-white rounded-xl border border-slate-200 overflow-hidden">
            {/* Sender row */}
            <div className="px-4 py-3 flex items-center gap-3 border-b border-slate-100">
              <Avatar name={detalle.creado_por_nombre} size="lg" />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-800">{detalle.creado_por_nombre || 'Sistema'}</p>
                <p className="text-[11px] text-slate-400 flex items-center gap-1">
                  <ArrowRight className="w-3 h-3" />
                  {detalle.destinatario_nombre || detalle.destinatario}
                </p>
              </div>
              <span
                className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                style={{ backgroundColor: tipoConf.bg, color: tipoConf.color }}
              >
                {tipoConf.label}
              </span>
            </div>

            {/* Message */}
            <div className="px-4 py-3">
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{detalle.mensaje}</p>
            </div>

            {/* Status and read info */}
            <div className="px-4 py-3 border-t border-slate-100 space-y-2">
              {/* Read status */}
              {detalle.leido_por && detalle.leido_por.length > 0 && (
                <div className="flex items-center gap-1.5 text-[11px] text-emerald-600">
                  <CheckCheck className="w-3.5 h-3.5" />
                  <span>Leido por {detalle.leido_por.map((l) => l.nombre).join(', ')}</span>
                </div>
              )}
              {detalle.leida && !detalle.leido_por?.length && detalle.fecha_lectura && (
                <div className="flex items-center gap-1.5 text-[11px] text-emerald-600">
                  <CheckCheck className="w-3.5 h-3.5" />
                  <span>Leido {formatFechaCompleta(detalle.fecha_lectura)}</span>
                </div>
              )}
              {!detalle.leida && esMia && (
                <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                  <Clock className="w-3.5 h-3.5" />
                  <span>No leido aun</span>
                </div>
              )}
            </div>
          </div>

          {/* Status buttons */}
          <div className="mx-3 mb-3 bg-white rounded-xl border border-slate-200 p-3">
            <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-2">Estado</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(ESTADO_CONFIG).map(([est, cfg]) => {
                const activo = (detalle.estado || 'pendiente') === est;
                return (
                  <button
                    key={est}
                    onClick={() => !activo && cambiarEstado(detalle.id, est)}
                    disabled={activo}
                    className={`px-3 py-2.5 sm:py-1.5 rounded-lg text-sm sm:text-xs font-medium transition-all ${
                      activo
                        ? `${cfg.color} ring-1 ${cfg.ring} shadow-sm`
                        : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                    }`}
                    data-testid={`notif-estado-${est}`}
                  >
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${cfg.dot} mr-1.5`} />
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Comments thread */}
          <div className="mx-3 mb-3 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-100">
              <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" />
                Seguimiento ({comentarios.length})
              </p>
            </div>
            {comentarios.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-6">Sin comentarios</p>
            ) : (
              <div className="p-3 space-y-2">
                {comentarios.map((c) => (
                  <div key={c.id} className={`rounded-lg p-2.5 ${c.es_sistema ? 'bg-slate-50' : 'bg-emerald-50/60'}`}>
                    {!c.es_sistema && (
                      <div className="flex items-center gap-2 mb-1">
                        <Avatar name={c.autor_nombre} size="sm" />
                        <span className="text-xs font-semibold text-slate-700">{c.autor_nombre}</span>
                      </div>
                    )}
                    <p className={`text-xs ${c.es_sistema ? 'text-slate-400 italic' : 'text-slate-600'}`}>
                      {c.texto}
                    </p>
                    <span className="text-[10px] text-slate-400 mt-1 block">{formatFechaCompleta(c.fecha)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Comment input */}
        <div className="p-3 bg-white border-t border-slate-200">
          <div className="flex gap-2 items-center">
            <Avatar name={user?.nombre} size="sm" />
            <div className="flex-1 flex gap-2">
              <input
                type="text"
                value={nuevoComentario}
                onChange={(e) => setNuevoComentario(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && enviarComentario(detalle.id)}
                placeholder="Escribe un comentario..."
                className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white transition-colors"
                data-testid="notif-comentario-input"
              />
              <button
                onClick={() => enviarComentario(detalle.id)}
                disabled={enviandoComentario || !nuevoComentario.trim()}
                className="px-3 py-2 bg-duetto-primary text-white rounded-lg hover:bg-emerald-700 disabled:opacity-40 transition-colors"
                data-testid="notif-comentario-enviar"
              >
                {enviandoComentario ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════════════════ */
  /*  NOTIFICATION CARD                               */
  /* ═══════════════════════════════════════════════ */
  const renderNotifCard = (notif, esEnviada = false) => {
    const tipoConf = TIPO_CONFIG[notif.tipo] || TIPO_CONFIG.info;
    const prioridadStyle = PRIORIDAD_STYLES[notif.prioridad] || PRIORIDAD_STYLES.normal;
    const estado = ESTADO_CONFIG[notif.estado || 'pendiente'];
    const numComentarios = (notif.comentarios || []).filter((c) => !c.es_sistema).length;
    const senderName = esEnviada ? (notif.destinatario_nombre || notif.destinatario) : notif.creado_por_nombre;

    return (
      <div
        key={notif.id}
        className={`group mx-3 mb-2 rounded-xl border border-slate-200 border-l-[3px] ${prioridadStyle.border} ${prioridadStyle.bg} bg-white hover:shadow-md hover:border-slate-300 transition-all duration-200 cursor-pointer overflow-hidden`}
        onClick={() => {
          cargarDetalle(notif.id);
          if (!notif.leida && !esEnviada) marcarLeida(notif.id);
        }}
        data-testid={`notif-card-${notif.id}`}
      >
        <div className="p-3">
          <div className="flex gap-3">
            {/* Avatar */}
            <Avatar name={senderName} size="md" />

            {/* Content */}
            <div className="flex-1 min-w-0">
              {/* Top row: title + time */}
              <div className="flex justify-between items-start gap-2 mb-0.5">
                <h4
                  className={`text-sm leading-tight truncate ${
                    notif.leida ? 'font-normal text-slate-500' : 'font-semibold text-slate-800'
                  }`}
                >
                  {notif.titulo}
                </h4>
                <span className="text-[10px] text-slate-400 whitespace-nowrap flex-shrink-0 mt-0.5">
                  {formatFecha(notif.fecha_creacion)}
                </span>
              </div>

              {/* Sender name */}
              <p className="text-[11px] text-slate-400 mb-1">
                {esEnviada ? `Para: ${notif.destinatario_nombre || notif.destinatario}` : (senderName || 'Sistema')}
              </p>

              {/* Message preview */}
              <p className={`text-xs leading-relaxed line-clamp-2 ${notif.leida ? 'text-slate-400' : 'text-slate-600'}`}>
                {notif.mensaje}
              </p>

              {/* Bottom row: badges */}
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {/* Status pill */}
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium ${estado.color}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${estado.dot}`} />
                  {estado.label}
                </span>
                {/* Type pill */}
                <span
                  className="text-[10px] font-medium px-2 py-0.5 rounded-md"
                  style={{ backgroundColor: tipoConf.bg, color: tipoConf.color }}
                >
                  {tipoConf.label}
                </span>
                {/* Read status for sent messages */}
                {esEnviada && (
                  notif.leida ? (
                    <span className="text-[10px] text-emerald-600 flex items-center gap-0.5">
                      <CheckCheck className="w-3 h-3" /> Leido
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                      <Check className="w-3 h-3" /> Enviado
                    </span>
                  )
                )}
                {/* Comments count */}
                {numComentarios > 0 && (
                  <span className="text-[10px] text-duetto-primary flex items-center gap-0.5 ml-auto">
                    <MessageSquare className="w-3 h-3" />
                    {numComentarios}
                  </span>
                )}
                {/* Unread dot */}
                {!notif.leida && !esEnviada && (
                  <span className="w-2 h-2 rounded-full bg-duetto-primary ml-auto flex-shrink-0" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════════════════ */
  /*  NEW NOTIFICATION FORM                           */
  /* ═══════════════════════════════════════════════ */
  const renderFormulario = () => (
    <div className="mx-3 mb-3 bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-100 flex justify-between items-center">
        <p className="text-xs font-semibold text-slate-600">Nuevo aviso</p>
        <button onClick={() => setMostrarFormulario(false)} className="p-1 hover:bg-slate-100 rounded">
          <X className="w-3.5 h-3.5 text-slate-400" />
        </button>
      </div>
      <form onSubmit={enviarAviso} className="p-3 space-y-2.5">
        <AvisoInline aviso={avisoForm} onCerrar={limpiarAvisoForm} testId="nuevo-aviso-error" />
        <select
          value={nuevoAviso.destinatario}
          onChange={(e) => setNuevoAviso({ ...nuevoAviso, destinatario: e.target.value })}
          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:bg-white"
          required
          data-testid="nuevo-destinatario"
        >
          <option value="">Enviar a...</option>
          <optgroup label="Grupos">
            {destinatarios.grupos.map((g) => (
              <option key={g.id} value={g.id}>{g.nombre}</option>
            ))}
          </optgroup>
          <optgroup label="Empleados">
            {destinatarios.empleados.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.nombre} {emp.puesto ? `(${emp.puesto})` : ''}
              </option>
            ))}
          </optgroup>
        </select>

        <div className="grid grid-cols-2 gap-2">
          <select
            value={nuevoAviso.tipo}
            onChange={(e) => setNuevoAviso({ ...nuevoAviso, tipo: e.target.value })}
            className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500"
          >
            {Object.entries(TIPO_CONFIG)
              .filter(([k]) => k !== 'asignacion_evento')
              .map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
          </select>
          <select
            value={nuevoAviso.prioridad}
            onChange={(e) => setNuevoAviso({ ...nuevoAviso, prioridad: e.target.value })}
            className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500"
          >
            <option value="baja">Baja</option>
            <option value="normal">Normal</option>
            <option value="alta">Alta</option>
            <option value="urgente">Urgente</option>
          </select>
        </div>

        <input
          type="text"
          value={nuevoAviso.titulo}
          onChange={(e) => setNuevoAviso({ ...nuevoAviso, titulo: e.target.value })}
          placeholder="Titulo (opcional)"
          className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:bg-white"
        />
        <textarea
          value={nuevoAviso.mensaje}
          onChange={(e) => setNuevoAviso({ ...nuevoAviso, mensaje: e.target.value })}
          placeholder="Mensaje..."
          rows={2}
          className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:bg-white resize-none"
          required
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMostrarFormulario(false)}
            className="flex-1 py-2 border border-slate-200 rounded-lg text-xs text-slate-500 hover:bg-slate-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={enviando}
            className="flex-1 py-2 bg-duetto-primary text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1.5 transition-colors"
            data-testid="enviar-aviso-btn"
          >
            <Send className="w-3.5 h-3.5" /> {enviando ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </form>
    </div>
  );

  /* ═══════════════════════════════════════════════ */
  /*  PANEL (Portal)                                  */
  /* ═══════════════════════════════════════════════ */
  const renderPanel = () => {
    const listaActual = tab === 'recibidas' ? notificaciones : enviadas;
    const completadasCount = numCompletadas(listaActual);
    const listaVisible = listaFiltrada(listaActual);

    return createPortal(
      <div className="fixed inset-0 z-[60] flex" data-testid="notificaciones-panel">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
          onClick={() => { setMostrarPanel(false); setDetalle(null); }}
        />

        {/* Panel container */}
        <div className="fixed inset-0 sm:absolute sm:inset-auto sm:left-64 sm:top-0 sm:bottom-0 sm:w-[420px] bg-slate-50 shadow-2xl flex flex-col z-[61] sm:border-r sm:border-slate-200">
          {detalle ? renderDetalle() : (
            <>
              {/* ── Header ── */}
              <div className="bg-white border-b border-slate-200">
                <div className="px-4 py-3 flex justify-between items-center">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-duetto-primary flex items-center justify-center">
                      <Bell className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm text-slate-800">Avisos y Tareas</h3>
                      {noLeidas > 0 && (
                        <p className="text-[11px] text-duetto-primary font-medium">{noLeidas} sin leer</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {noLeidas > 0 && tab === 'recibidas' && (
                      <button
                        onClick={marcarTodasLeidas}
                        className="p-2.5 hover:bg-slate-100 rounded-lg transition-colors"
                        title="Marcar todas como leidas"
                        aria-label="Marcar todas como leídas"
                        data-testid="marcar-todas-leidas"
                      >
                        <CheckCheck className="w-5 h-5 text-slate-500" />
                      </button>
                    )}
                    <button
                      onClick={() => { setMostrarPanel(false); setDetalle(null); }}
                      className="p-2.5 hover:bg-slate-100 rounded-lg transition-colors"
                      aria-label="Cerrar avisos"
                      data-testid="cerrar-panel"
                    >
                      <X className="w-5 h-5 text-slate-500" />
                    </button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="px-4 pb-3 flex gap-1">
                  <button
                    onClick={() => setTab('recibidas')}
                    className={`flex-1 py-2.5 rounded-lg text-sm sm:text-xs font-semibold transition-all ${
                      tab === 'recibidas'
                        ? 'bg-duetto-primary text-white shadow-sm'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                    data-testid="tab-recibidas"
                  >
                    Recibidas
                    {noLeidas > 0 && (
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                        tab === 'recibidas' ? 'bg-white/20 text-white' : 'bg-red-500 text-white'
                      }`}>
                        {noLeidas}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setTab('enviadas')}
                    className={`flex-1 py-2.5 rounded-lg text-sm sm:text-xs font-semibold transition-all ${
                      tab === 'enviadas'
                        ? 'bg-duetto-primary text-white shadow-sm'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                    data-testid="tab-enviadas"
                  >
                    Enviadas
                  </button>
                </div>
              </div>

              {/* ── Toolbar ── */}
              <div className="px-3 py-2 flex items-center gap-2 bg-white border-b border-slate-200">
                <button
                  onClick={() => setMostrarFormulario(!mostrarFormulario)}
                  className={`flex items-center gap-1.5 px-3 py-2.5 sm:py-1.5 rounded-lg text-sm sm:text-xs font-semibold transition-all ${
                    mostrarFormulario
                      ? 'bg-duetto-primary text-white shadow-sm'
                      : 'bg-emerald-50 text-duetto-primary hover:bg-emerald-100'
                  }`}
                  data-testid="nuevo-aviso-btn"
                >
                  <Plus className="w-3.5 h-3.5" /> Nuevo
                </button>

                {/* State filter */}
                <div className="relative ml-auto">
                  <button
                    onClick={() => setMostrarFiltros(!mostrarFiltros)}
                    className={`flex items-center gap-1.5 px-2.5 py-2.5 sm:py-1.5 rounded-lg text-sm sm:text-xs font-medium transition-colors ${
                      filtroEstado !== 'todos'
                        ? 'bg-violet-50 text-violet-700'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                    data-testid="filtro-toggle"
                  >
                    <Filter className="w-3.5 h-3.5" />
                    {filtroEstado !== 'todos' ? ESTADO_CONFIG[filtroEstado]?.label : 'Filtrar'}
                  </button>
                  {mostrarFiltros && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-10 w-40 overflow-hidden">
                      <button
                        onClick={() => { setFiltroEstado('todos'); setMostrarFiltros(false); }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 transition-colors ${filtroEstado === 'todos' ? 'font-semibold text-duetto-primary bg-emerald-50/50' : ''}`}
                      >
                        Todos
                      </button>
                      {Object.entries(ESTADO_CONFIG).map(([key, cfg]) => (
                        <button
                          key={key}
                          onClick={() => { setFiltroEstado(key); setMostrarFiltros(false); }}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2 transition-colors ${
                            filtroEstado === key ? 'font-semibold bg-emerald-50/50' : ''
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                          {cfg.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Preferencias: qué avisos ver */}
                <button
                  onClick={() => setMostrarPrefs((v) => !v)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    mostrarPrefs
                      ? 'bg-emerald-50 text-duetto-primary'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                  title="Elegir qué avisos ver"
                  data-testid="btn-prefs-notificaciones"
                >
                  <Settings className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* ── Preferencias de tipos visibles ── */}
              {mostrarPrefs && prefs && (
                <div className="px-4 py-3 border-b bg-gray-50 space-y-1.5" data-testid="prefs-notificaciones">
                  <p className="text-xs font-semibold text-gray-600 mb-1">Qué avisos quieres ver</p>
                  {prefs.tipos.map((t) => (
                    <label key={t.tipo} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={!prefs.tipos_ocultos.includes(t.tipo)}
                        onChange={() => toggleTipoOculto(t.tipo)}
                        className="rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                      />
                      {t.label}
                    </label>
                  ))}
                  <p className="text-[10px] text-gray-400 pt-1">Los avisos ocultos no se borran: vuelven a verse al reactivar la casilla.</p>
                </div>
              )}

              {/* ── Body ── */}
              <div className="flex-1 overflow-y-auto py-3">
                {/* New form */}
                {mostrarFormulario && renderFormulario()}

                {/* Completed toggle */}
                {completadasCount > 0 && (
                  <button
                    onClick={() => setMostrarCompletadas(!mostrarCompletadas)}
                    className="mx-3 mb-2 w-[calc(100%-1.5rem)] px-3 py-2 text-xs text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg flex items-center justify-center gap-1.5 border border-dashed border-slate-200 transition-colors"
                    data-testid="toggle-completadas"
                  >
                    {mostrarCompletadas ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    {mostrarCompletadas ? 'Ocultar' : 'Mostrar'} completadas ({completadasCount})
                  </button>
                )}

                {/* List */}
                {listaVisible.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                      {tab === 'recibidas' ? (
                        <Bell className="w-5 h-5 text-slate-300" />
                      ) : (
                        <Send className="w-5 h-5 text-slate-300" />
                      )}
                    </div>
                    <p className="text-sm text-slate-400">
                      {filtroEstado !== 'todos' ? 'Sin resultados para este filtro' : tab === 'recibidas' ? 'No hay avisos' : 'No has enviado avisos'}
                    </p>
                  </div>
                ) : (
                  listaVisible.map((n) => renderNotifCard(n, tab === 'enviadas'))
                )}
              </div>
            </>
          )}
        </div>
      </div>,
      document.body
    );
  };

  /* ═══════════════════════════════════════════════ */
  /*  RENDER                                          */
  /* ═══════════════════════════════════════════════ */
  return (
    <div className="relative">
      {/* Sidebar button */}
      <button
        onClick={() => { setMostrarPanel(!mostrarPanel); setDetalle(null); }}
        className={`flex items-center gap-2 w-full p-3 rounded-lg transition-colors ${
          mostrarPanel ? 'bg-emerald-50 text-duetto-primary' : 'hover:bg-gray-100 text-gray-700'
        }`}
        data-testid="notificaciones-toggle"
        data-tour="notificaciones"
      >
        <div className="relative">
          <Bell className="w-5 h-5" />
          {noLeidas > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] w-4.5 h-4.5 min-w-[18px] min-h-[18px] rounded-full flex items-center justify-center font-bold leading-none">
              {noLeidas > 9 ? '9+' : noLeidas}
            </span>
          )}
        </div>
        {!collapsed && <span className="font-medium">Notificaciones</span>}
        {!collapsed && noLeidas > 0 && (
          <span className="ml-auto bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-medium">
            {noLeidas}
          </span>
        )}
      </button>

      {/* Panel */}
      {mostrarPanel && renderPanel()}

      <DialogoConfirmar
        abierto={!!aEliminar}
        titulo="Eliminar aviso"
        descripcion="Se eliminará este aviso para todos. No se puede deshacer."
        textoConfirmar="Eliminar"
        peligro
        ocupado={eliminando}
        onConfirmar={confirmarEliminar}
        onCancelar={() => setAEliminar(null)}
        testId="notif-confirmar-eliminar"
      />

      {/* Toasts in-app de correo nuevo */}
      {createPortal(
        <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 w-80 max-w-[calc(100vw-2rem)]">
          {emailToasts.map((t) => (
            <div
              key={t.id}
              className="bg-white rounded-xl shadow-lg border border-l-4 border-l-[#1A9169] p-3 cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => { cerrarEmailToast(t.id); navigate(`/emails?email=${t.id}`); }}
              data-testid={`toast-email-${t.id}`}
            >
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-full bg-[#1A9169]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-[#1A9169]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-800 truncate">{t.remitente || 'Correo nuevo'}</p>
                  <p className="text-xs text-gray-700 truncate">{t.asunto}</p>
                  {t.snippet && <p className="text-xs text-gray-400 truncate mt-0.5">{t.snippet}</p>}
                </div>
                <button
                  onClick={(ev) => { ev.stopPropagation(); cerrarEmailToast(t.id); }}
                  className="p-1 text-gray-300 hover:text-gray-500 flex-shrink-0"
                  aria-label="Cerrar aviso"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificacionesSidebar;
